import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { it } from "date-fns/locale";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "./Button";
import { Modal } from "./Modal";

export type CalendarView = "week" | "month" | "year";

interface CalendarBandProps<T> {
  items: T[];
  getId: (item: T) => string;
  /** Data effettiva dell'elemento; null/"" = senza data (non compare nella fascia). */
  getDate: (item: T) => string | Date | null | undefined;
  /** Colore del pallino (hex) — per stato (Installazioni) o per tipo (Calendario unificato). */
  getColor: (item: T) => string;
  getTitle: (item: T) => string;
  getSubtitle?: (item: T) => string | null | undefined;
  /**
   * L'orario dell'appuntamento, già composto: "09:30 – 12:00", "dalle 09:30",
   * o null/undefined se l'elemento non ne ha (o se il modulo non ha orari).
   *
   * Fornirla accende due cose insieme: l'orario compare sulle tessere, e le
   * tessere dello stesso giorno si mettono in fila per ora d'inizio. Un
   * calendario che mostra le 14:00 sopra le 09:00 si legge come un guasto.
   * Chi non la passa vede il componente esattamente com'era prima, ordine
   * d'arrivo compreso.
   */
  getOrario?: (item: T) => string | null | undefined;
  /** Badge/contenuto extra mostrato nella card settimanale (es. stato + in ritardo). */
  renderBadges?: (item: T) => ReactNode;
  /**
   * Icona di STATO in fondo alla riga del titolo, dopo l'etichetta. Si affianca
   * al pallino di `getColor` senza toccarlo: quello dice DI CHE COSA si tratta,
   * questa dice A CHE PUNTO è.
   *
   * Va a fine riga e non sul pallino perché così sta sempre nello stesso punto
   * qualunque sia la lunghezza del titolo — che è troncato con l'ellissi e su
   * mobile lo è quasi sempre. Il chiamante restituisce l'icona già pronta
   * (dimensione, colore, testo alternativo); qui si aggiungono solo `ml-auto`,
   * che la tiene a filo del bordo destro qualunque sia la lunghezza del titolo,
   * e `shrink-0`, che le impedisce di farsi comprimere da un titolo lungo.
   *
   * Può restituire `null` per un elemento che non ha niente da dire sul
   * proprio stato: in quel caso la riga resta senza icona, non con uno spazio
   * vuoto al posto suo.
   *
   * Chi non la passa vede la riga esattamente com'era prima.
   */
  renderStato?: (item: T) => ReactNode;
  onItemClick: (item: T) => void;
  /** Viste disponibili nel toggle. Default: Settimana + Mese. */
  views?: CalendarView[];
  /** Se fornito, mostra un "+" per aggiungere un elemento a quel giorno (ISO yyyy-MM-dd). */
  onAddDay?: (isoDate: string) => void;
  /** Notifica il range visibile corrente (per export per periodo, o per scriverlo nell'URL). */
  onRangeChange?: (from: Date, to: Date, view: CalendarView) => void;
  /**
   * Vista e giorno da cui partire, al montaggio: servono a riaprire il
   * calendario dov'era (Installazioni li tiene nell'URL). Dopo, vista e periodo
   * sono del componente come prima, e si seguono con `onRangeChange`. Una vista
   * fuori da `views` viene ignorata.
   */
  vistaIniziale?: CalendarView;
  dataIniziale?: Date;
  /**
   * Se true, le celle-giorno accettano il rilascio di una tessera trascinata.
   * Il DndContext e la logica di drop vivono nel chiamante: qui si registrano
   * solo i bersagli, così il componente resta identico a prima per chi non usa
   * il trascinamento. Non ha effetto in vista Anno, che non ha giorni.
   */
  droppable?: boolean;
  /**
   * Se fornita, le tessere già collocate in un giorno diventano trascinabili
   * su un altro giorno — ma solo quelle per cui la funzione ritorna true.
   * Serve ai moduli che non passano dalla pila "senza data" (i sopralluoghi
   * hanno sempre una data): per loro spostare è l'unica azione possibile.
   */
  getCanEdit?: (item: T) => boolean;
  /**
   * Perché una tessera NON è spostabile, quando il motivo è una regola di
   * dominio e non un permesso. Mostrato come tooltip sulla tessera spenta:
   * senza, il trascinamento semplicemente non risponde e sembra un guasto.
   */
  getMotivoBlocco?: (item: T) => string | null;
  isLoading?: boolean;
}

const VIEW_LABEL: Record<CalendarView, string> = { week: "Settimana", month: "Mese", year: "Anno" };

/** Prefisso degli id droppable, per non collidere con altri DndContext in pagina. */
export const DROP_GIORNO_PREFIX = "giorno:";

/** Prefisso degli id delle tessere già collocate in un giorno. */
export const DRAG_TESSERA_PREFIX = "tessera:";

/**
 * Tessera dentro una cella-giorno, trascinabile su un altro giorno per
 * spostarne la data. Serve per i moduli che non passano mai dalla pila
 * "senza data" — i sopralluoghi hanno `data_sopralluogo` NOT NULL, quindi per
 * loro questo è l'unico modo di spostare un appuntamento.
 *
 * Il trascinamento parte da tutta la tessera e non da una maniglia, come
 * invece nella pila: qui lo spazio è di pochi pixel. Il click resta un click
 * grazie al vincolo di attivazione a distanza impostato dal chiamante.
 */
function TesseraTrascinabile({
  id,
  attivo,
  motivoBlocco,
  dati,
  onClick,
  className,
  children,
}: {
  id: string;
  attivo: boolean;
  motivoBlocco?: string | null;
  dati: unknown;
  onClick: () => void;
  className: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${DRAG_TESSERA_PREFIX}${id}`,
    data: { scheda: dati },
    disabled: !attivo,
  });

  // Il tooltip compare solo quando il blocco ha una spiegazione da dare: se
  // manca il permesso il titolo resta vuoto, com'era prima.
  const bloccataConMotivo = !attivo && !!motivoBlocco;

  return (
    <button
      ref={attivo ? setNodeRef : undefined}
      onClick={onClick}
      title={bloccataConMotivo ? motivoBlocco! : undefined}
      {...(attivo ? listeners : {})}
      {...(attivo ? attributes : {})}
      className={`${className} ${attivo ? "cursor-grab touch-none active:cursor-grabbing" : ""} ${
        bloccataConMotivo ? "cursor-not-allowed" : ""
      } ${isDragging ? "opacity-40" : ""}`}
    >
      {children}
    </button>
  );
}

/**
 * Cella-giorno che accetta il rilascio di una tessera. È un componente a sé
 * perché useDroppable è un hook: non può stare dentro il .map() delle celle.
 * Se il calendario non è in modalità drop (`attivo` false) non registra nulla
 * e si comporta come un normale contenitore.
 */
function GiornoDroppable({
  isoDate,
  attivo,
  className,
  classNameSopra,
  children,
}: {
  isoDate: string;
  attivo: boolean;
  className: string;
  /** Classi aggiunte quando una tessera è sospesa sopra la cella. */
  classNameSopra: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${DROP_GIORNO_PREFIX}${isoDate}`, disabled: !attivo });
  return (
    <div ref={attivo ? setNodeRef : undefined} className={`${className} ${attivo && isOver ? classNameSopra : ""}`}>
      {children}
    </div>
  );
}

/**
 * L'ora d'inizio dentro un orario già composto: "09:30" da "09:30 – 12:00" e
 * da "dalle 09:30", "12:00" da "fino alle 12:00".
 *
 * Serve a due cose che nella vista Mese non stanno larghe: mettere in fila le
 * tessere di un giorno, e mostrare l'ora in una cella dove l'intervallo intero
 * mangerebbe tutto lo spazio del titolo. La forma estesa resta nella vista
 * Settimana e nel dettaglio del giorno, dove c'è posto.
 *
 * `null` se non c'è nessun orario: quelle tessere vanno in fondo al giorno,
 * nell'ordine in cui sono arrivate.
 */
function oraInizio(orario: string | null | undefined): string | null {
  if (!orario) return null;
  return /\d{2}:\d{2}/.exec(orario)?.[0] ?? null;
}

/**
 * Involucro dell'icona di stato a fine riga. Esiste per non lasciare un `span`
 * vuoto — e quindi un `ml-auto` che sposta il titolo — quando `renderStato`
 * decide che quell'elemento non ha nessuno stato da mostrare.
 */
function StatoInRiga({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <span className="ml-auto shrink-0 leading-none">{children}</span>;
}

function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Fascia calendario riusabile (Settimana / Mese / Anno). Generica sul tipo T:
 * il chiamante fornisce come ricavare data/colore/titolo e cosa fare al click.
 * Usata da "Installazioni" (colore per stato, Settimana/Mese) e dal Calendario
 * unificato (colore per tipo, Settimana/Mese/Anno).
 */
export function CalendarBand<T>({
  items,
  getId,
  getDate,
  getColor,
  getTitle,
  getSubtitle,
  getOrario,
  renderBadges,
  renderStato,
  onItemClick,
  views = ["week", "month"],
  onAddDay,
  onRangeChange,
  vistaIniziale,
  dataIniziale,
  droppable = false,
  getCanEdit,
  getMotivoBlocco,
  isLoading,
}: CalendarBandProps<T>) {
  const [view, setView] = useState<CalendarView>(() =>
    vistaIniziale && views.includes(vistaIniziale) ? vistaIniziale : views[0] ?? "week"
  );
  const [weekStart, setWeekStart] = useState(() => startOfWeek(dataIniziale ?? new Date(), { weekStartsOn: 1 }));
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(dataIniziale ?? new Date()));
  const [yearAnchor, setYearAnchor] = useState(() => startOfYear(dataIniziale ?? new Date()));
  const [dayDetail, setDayDetail] = useState<Date | null>(null);

  // Item con data valida, raggruppati per giorno (chiave yyyy-MM-dd).
  const byDay = useMemo(() => {
    const map = new Map<string, T[]>();
    for (const it of items) {
      const d = toDate(getDate(it));
      if (!d) continue;
      const key = format(d, "yyyy-MM-dd");
      const arr = map.get(key);
      if (arr) arr.push(it);
      else map.set(key, [it]);
    }
    // In fila per ora d'inizio, ma solo se il chiamante dichiara di avere
    // orari: senza `getOrario` l'ordine resta quello d'arrivo, identico a
    // prima. Chi non ha un orario finisce in fondo — `sort` in JS è stabile,
    // quindi fra loro l'ordine originale non cambia.
    if (getOrario) {
      for (const arr of map.values()) {
        arr.sort((a, b) => {
          const oa = oraInizio(getOrario(a));
          const ob = oraInizio(getOrario(b));
          if (oa && ob) return oa.localeCompare(ob);
          if (oa) return -1;
          if (ob) return 1;
          return 0;
        });
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  function itemsForDay(day: Date): T[] {
    return byDay.get(format(day, "yyyy-MM-dd")) ?? [];
  }
  function countInRange(from: Date, to: Date): number {
    let n = 0;
    for (const [key, arr] of byDay) {
      const d = new Date(key);
      if (d >= from && d <= to) n += arr.length;
    }
    return n;
  }

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const monthDays = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(monthAnchor), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(monthAnchor), { weekStartsOn: 1 });
    const days: Date[] = [];
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);
    return days;
  }, [monthAnchor]);
  const yearMonths = useMemo(
    () => Array.from({ length: 12 }, (_, i) => addMonths(startOfYear(yearAnchor), i)),
    [yearAnchor]
  );

  // Comunica il range visibile (per l'export per periodo).
  useEffect(() => {
    if (!onRangeChange) return;
    if (view === "week") onRangeChange(weekStart, endOfWeek(weekStart, { weekStartsOn: 1 }), view);
    else if (view === "month") onRangeChange(startOfMonth(monthAnchor), endOfMonth(monthAnchor), view);
    else onRangeChange(startOfYear(yearAnchor), endOfYear(yearAnchor), view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, weekStart, monthAnchor, yearAnchor]);

  function goToday() {
    if (view === "week") setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    else if (view === "month") setMonthAnchor(startOfMonth(new Date()));
    else setYearAnchor(startOfYear(new Date()));
  }
  function goPrev() {
    if (view === "week") setWeekStart((d) => subWeeks(d, 1));
    else if (view === "month") setMonthAnchor((d) => subMonths(d, 1));
    else setYearAnchor((d) => subYears(d, 1));
  }
  function goNext() {
    if (view === "week") setWeekStart((d) => addWeeks(d, 1));
    else if (view === "month") setMonthAnchor((d) => addMonths(d, 1));
    else setYearAnchor((d) => addYears(d, 1));
  }

  const periodLabel =
    view === "week"
      ? `${format(weekStart, "d MMMM", { locale: it })} – ${format(addDays(weekStart, 6), "d MMMM yyyy", { locale: it })}`
      : view === "month"
        ? format(monthAnchor, "MMMM yyyy", { locale: it })
        : format(yearAnchor, "yyyy", { locale: it });

  return (
    <div>
      {/* Barra di navigazione della fascia */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-line bg-white p-0.5">
          {views.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === v ? "bg-primary text-white" : "text-ink/50 hover:text-ink"
              }`}
            >
              {VIEW_LABEL[v]}
            </button>
          ))}
        </div>
        <Button variant="secondary" size="sm" onClick={goPrev} aria-label="Periodo precedente">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button variant="secondary" size="sm" onClick={goToday}>
          Oggi
        </Button>
        <Button variant="secondary" size="sm" onClick={goNext} aria-label="Periodo successivo">
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="ml-1 text-sm font-medium capitalize text-ink/60">{periodLabel}</span>
      </div>

      {isLoading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : view === "week" ? (
        // --- Vista settimanale: card complete per giorno ---
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
          {weekDays.map((day) => {
            const dayItems = itemsForDay(day);
            const isToday = isSameDay(day, new Date());
            return (
              <GiornoDroppable
                key={day.toISOString()}
                isoDate={format(day, "yyyy-MM-dd")}
                attivo={droppable}
                className="flex flex-col rounded-card border border-line bg-white shadow-card transition-colors"
                classNameSopra="border-primary bg-primary/5 ring-2 ring-primary/30"
              >
                <div className={`border-b border-line px-3 py-2 text-center ${isToday ? "bg-primary/5" : ""}`}>
                  <p className="text-xs font-semibold uppercase text-ink/50">{format(day, "EEE", { locale: it })}</p>
                  <p className={`text-lg font-bold ${isToday ? "text-primary" : "text-ink"}`}>{format(day, "d")}</p>
                </div>
                <div className="flex-1 space-y-2 p-2">
                  {dayItems.map((item) => (
                    <TesseraTrascinabile
                      key={getId(item)}
                      id={getId(item)}
                      attivo={!!getCanEdit?.(item)}
                      motivoBlocco={getMotivoBlocco?.(item) ?? null}
                      dati={item}
                      onClick={() => onItemClick(item)}
                      className="w-full rounded-lg border border-line p-2 text-left text-xs transition-colors hover:bg-primary/5"
                    >
                      <p className="flex items-center gap-1 truncate font-semibold text-ink">
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: getColor(item) }}
                        />
                        <span className="truncate">{getTitle(item)}</span>
                        {/* Dopo il titolo e fuori dal `truncate`: l'icona resta
                            visibile anche quando l'etichetta va in ellissi. */}
                        <StatoInRiga>{renderStato?.(item)}</StatoInRiga>
                      </p>
                      {/* Sopra il sottotitolo e non accanto al titolo: qui c'è
                          spazio per l'intervallo intero, ed è la prima cosa
                          che si cerca guardando un giorno. */}
                      {getOrario?.(item) && (
                        <p className="truncate font-semibold tabular-nums text-primary">{getOrario(item)}</p>
                      )}
                      {getSubtitle?.(item) && <p className="truncate text-ink/50">{getSubtitle(item)}</p>}
                      {renderBadges && <div className="mt-1 flex flex-wrap gap-1">{renderBadges(item)}</div>}
                    </TesseraTrascinabile>
                  ))}
                  {onAddDay && (
                    <button
                      onClick={() => onAddDay(format(day, "yyyy-MM-dd"))}
                      className="flex w-full items-center justify-center rounded-lg border border-dashed border-line py-1.5 text-ink/30 transition-colors hover:border-primary hover:text-primary"
                      aria-label={`Aggiungi il ${format(day, "d MMMM", { locale: it })}`}
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </GiornoDroppable>
            );
          })}
        </div>
      ) : view === "month" ? (
        // --- Vista mensile: pallini colorati, "+N altre" oltre 3 ---
        <div>
          <div className="mb-1 grid grid-cols-7 gap-px overflow-hidden rounded-t-card bg-black/5 text-center text-xs font-semibold uppercase text-ink/50">
            {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
              <div key={d} className="bg-surface py-2">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-b-card border border-line bg-black/5">
            {monthDays.map((day) => {
              const dayItems = itemsForDay(day);
              const isToday = isSameDay(day, new Date());
              const inMonth = isSameMonth(day, monthAnchor);
              const visible = dayItems.slice(0, 3);
              const overflow = dayItems.length - visible.length;
              return (
                <GiornoDroppable
                  key={day.toISOString()}
                  isoDate={format(day, "yyyy-MM-dd")}
                  attivo={droppable}
                  className={`flex min-h-[92px] flex-col gap-1 bg-white p-1.5 transition-colors ${!inMonth ? "opacity-40" : ""}`}
                  classNameSopra="bg-primary/10 ring-2 ring-inset ring-primary/40"
                >
                  <span
                    className={`self-end text-xs font-semibold ${isToday ? "flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white" : "text-ink/60"}`}
                  >
                    {format(day, "d")}
                  </span>
                  <div className="flex flex-col gap-0.5">
                    {visible.map((item) => (
                      <TesseraTrascinabile
                        key={getId(item)}
                        id={getId(item)}
                        attivo={!!getCanEdit?.(item)}
                        motivoBlocco={getMotivoBlocco?.(item) ?? null}
                        dati={item}
                        onClick={() => onItemClick(item)}
                        className="flex items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[10px] font-medium text-ink hover:bg-black/5"
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: getColor(item) }} />
                        {/* Solo l'ora d'inizio: in una cella di questa
                            larghezza l'intervallo intero non lascerebbe posto
                            al titolo, che è ciò che identifica la tessera. */}
                        {oraInizio(getOrario?.(item)) && (
                          <span className="shrink-0 tabular-nums text-primary">{oraInizio(getOrario!(item))}</span>
                        )}
                        <span className="truncate">{getTitle(item)}</span>
                      </TesseraTrascinabile>
                    ))}
                    {overflow > 0 && (
                      <button
                        onClick={() => setDayDetail(day)}
                        className="px-1 text-left text-[10px] font-semibold text-primary hover:underline"
                      >
                        +{overflow} altre
                      </button>
                    )}
                    {dayItems.length === 0 && onAddDay && inMonth && (
                      <button
                        onClick={() => onAddDay(format(day, "yyyy-MM-dd"))}
                        aria-label={`Aggiungi il ${format(day, "d MMMM", { locale: it })}`}
                        className="flex items-center justify-center rounded py-0.5 text-ink/20 hover:text-primary"
                      >
                        <Plus className="h-3 w-3" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </GiornoDroppable>
              );
            })}
          </div>
        </div>
      ) : (
        // --- Vista annuale: 12 mesi, conteggio per mese, click -> vista Mese ---
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {yearMonths.map((m) => {
            const n = countInRange(startOfMonth(m), endOfMonth(m));
            return (
              <button
                key={m.toISOString()}
                onClick={() => {
                  setMonthAnchor(startOfMonth(m));
                  setView("month");
                }}
                className="flex items-center justify-between rounded-card border border-line bg-white p-4 text-left shadow-card transition-shadow hover:shadow-md"
              >
                <span className="text-sm font-semibold capitalize text-ink">{format(m, "MMMM", { locale: it })}</span>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${n > 0 ? "bg-primary/10 text-primary" : "bg-black/5 text-ink/40"}`}
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Dettaglio di un giorno con più di 3 elementi (vista mensile) */}
      <Modal
        open={dayDetail !== null}
        onClose={() => setDayDetail(null)}
        title={dayDetail ? format(dayDetail, "d MMMM yyyy", { locale: it }) : ""}
      >
        <div className="flex flex-col gap-2">
          {dayDetail &&
            itemsForDay(dayDetail).map((item) => (
              <button
                key={getId(item)}
                onClick={() => {
                  onItemClick(item);
                  setDayDetail(null);
                }}
                className="flex items-center gap-2 rounded-lg border border-line p-2 text-left text-sm hover:bg-primary/5"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: getColor(item) }} />
                {getOrario?.(item) && (
                  <span className="shrink-0 font-semibold tabular-nums text-primary">{getOrario(item)}</span>
                )}
                <span className="flex-1 truncate">
                  <span className="font-semibold text-ink">{getTitle(item)}</span>
                  {getSubtitle?.(item) && <span className="text-ink/50"> · {getSubtitle(item)}</span>}
                </span>
              </button>
            ))}
        </div>
      </Modal>
    </div>
  );
}
